/**
 * TAN-8655 — Resolución del título de página a partir de la ruta activa (WCAG 2.4.2).
 *
 * Módulo puro y sin React a propósito: la parte delicada (qué ruta gana y cómo se compone el
 * texto) queda testeable sin montar routers ni providers de i18n.
 */

import { matchPath } from 'react-router-dom';

import { stripHtml } from 'core/utils/utils';

import type { RouteConfig } from 'app/types/router';

/** Separador entre el título de página y el nombre de la aplicación (U+00B7). */
const TITLE_SEPARATOR = ' · ';

/**
 * Devuelve la clave i18n de título de la primera ruta que casa con `pathname`.
 *
 * Recorre `routes` EN ORDEN y se queda con el primer match, igual que hace `<Switch>` al pintar:
 * el orden de `routesConfig.js` es significativo (hay paths que se solapan, p. ej.
 * `/course/:guid/program` antes que `/course/:guid`).
 *
 * Por qué `isExact` se inyecta en vez de leer `route.exact`: los dos `renderRoutes` no coinciden.
 * El del router estándar pone `exact` solo cuando la ruta trae `props` (e ignora el `exact`
 * declarado en la config), y el de Edelvives lo pone siempre. Si aquí asumiéramos una de las dos
 * reglas, el título resuelto podría corresponder a una ruta distinta de la que realmente se pinta.
 * Cada router pasa su propia regla, y así ambas cosas no pueden divergir.
 *
 * @returns La entrada de `routesConfig` que casa, o `undefined` si ninguna.
 */
export function resolveRoute(pathname: string, routes: RouteConfig[], isExact: (route: RouteConfig) => boolean): RouteConfig | undefined {
  return routes?.find((route) => Boolean(route?.path) && matchPath(pathname, { path: route.path, exact: isExact(route) }));
}



/**
 * Une segmentos de título descartando los vacíos.
 *
 * El orden es significativo: **lo más específico primero**. Los navegadores truncan el título por
 * el final, así que poner el genérico delante cortaría justo lo que distingue una pestaña de otra
 * («Matemáticas 5º · Panel · Tenant», no «Panel · Matemáticas 5º · Tenant»).
 */
export function composeTitleSegments(segments: Array<string | undefined>): string {
  return segments
    .map((segment) => segment?.trim())
    .filter((segment): segment is string => Boolean(segment))
    .join(TITLE_SEPARATOR);
}

/**
 * Deriva el segmento de título a partir del nombre de un recurso: la lección, el curso, la
 * evaluación, el alumno…
 *
 * La comprobación previa no es defensiva de más. `stripHtml` asigna a `innerHTML`, que solo
 * coacciona `null` a `''`: con `undefined` devuelve la cadena literal `"undefined"`, y el título
 * diría «undefined · Lección · Tenant» durante toda la carga — precisamente lo que anuncia un
 * lector de pantalla al aterrizar.
 *
 * @returns El nombre sin marcado, o `undefined` si aún no hay dato (`<PageTitle>` deja entonces el
 *   título genérico de la ruta).
 */
export function pageTitleFromName(name?: string | null): string | undefined {
  return name ? stripHtml(name) : undefined;
}
